import { useState } from "react";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Textarea from "../../components/common/Textarea";
import Select from "../../components/common/Select";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Pagination from "../../components/common/Pagination";
import Table from "../../components/common/Table";
import ActionButton from "../../components/common/ActionButton";
import ActionCheckbox from "../../components/common/ActionCheckbox";
import { useStore } from "../../context/StoreContext";
import useClientPagination from "../../hooks/useClientPagination";
import {
  useGetPromotionsQuery,
  useCreatePromotionMutation,
  useUpdatePromotionMutation,
  useDeletePromotionMutation,
} from "../../store/api";

const Promotions = () => {
  const { selectedStoreId } = useStore();
  const {
    data: promotions,
    isLoading,
    isError,
    error,
  } = useGetPromotionsQuery(selectedStoreId, { skip: !selectedStoreId });

  const [createPromotion, createState] = useCreatePromotionMutation();
  const [updatePromotion, updateState] = useUpdatePromotionMutation();
  const [deletePromotion, deleteState] = useDeletePromotionMutation();

  const [form, setForm] = useState({
    name: "",
    description: "",
    promoCode: "",
    priority: 0,
    isActive: true,
    status: "Active",
    promoType: "Sale",
    stackingPolicy: "Allow",
  });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    promotionId: null,
    promotionName: "",
  });

  const promotionsList = Array.isArray(promotions) ? promotions : [];
  const {
    page,
    totalPages,
    totalCount,
    pageSize,
    paginatedItems: paginatedPromotions,
    setPage,
    setPageSize,
  } = useClientPagination(promotionsList, { pageSize: 10 });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStoreId) {
      alert("Please select a store first");
      return;
    }
    try {
      const payload = {
        ...form,
        priority: Number(form.priority),
      };
      if (editingId) {
        await updatePromotion({
          storeId: selectedStoreId,
          promotionId: editingId,
          payload,
        }).unwrap();
        setEditingId(null);
      } else {
        await createPromotion({ storeId: selectedStoreId, payload }).unwrap();
      }
      setForm({
        name: "",
        description: "",
        promoCode: "",
        priority: 0,
        isActive: true,
        status: "Active",
        promoType: "Sale",
        stackingPolicy: "Allow",
      });
      setShowForm(false);
    } catch (err) {
      // error handled by UI
    }
  };

  const handleEdit = (promotion) => {
    setEditingId(promotion.promotionId || promotion.id);
    setForm({
      name: promotion.name || promotion.promotionName || "",
      description: promotion.description || "",
      promoCode: promotion.promoCode || promotion.code || "",
      priority: promotion.priority || 0,
      isActive: promotion.isActive !== undefined ? promotion.isActive : true,
      status: promotion.status || "Active",
      promoType: promotion.promoType || "Sale",
      stackingPolicy: promotion.stackingPolicy || "Allow",
    });
    setShowForm(true);
  };

  const handleDelete = (promotion) => {
    setDeleteModal({
      isOpen: true,
      promotionId: promotion.promotionId || promotion.id,
      promotionName: promotion.name || promotion.promotionName || "this promotion",
    });
  };

  const confirmDelete = async () => {
    try {
      await deletePromotion({
        storeId: selectedStoreId,
        promotionId: deleteModal.promotionId,
      }).unwrap();
    } catch (err) {
      // error handled by UI
    } finally {
      setDeleteModal({
        isOpen: false,
        promotionId: null,
        promotionName: "",
      });
    }
  };

  const handleToggleActive = async (promotion) => {
    const promotionId = promotion.promotionId || promotion.id;
    try {
      await updatePromotion({
        storeId: selectedStoreId,
        promotionId,
        payload: {
          name: promotion.name || promotion.promotionName || "",
          description: promotion.description || "",
          promoCode: promotion.promoCode || promotion.code || "",
          priority: Number(promotion.priority ?? 0),
          isActive: !(promotion.isActive !== undefined
            ? promotion.isActive
            : (promotion.status || "Active") === "Active"),
          status:
            promotion.status || (promotion.isActive ? "Active" : "Inactive"),
          promoType: promotion.promoType || "Sale",
          stackingPolicy: promotion.stackingPolicy || "Allow",
        },
      }).unwrap();
    } catch (err) {
      // error handled by UI
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm({
      name: "",
      description: "",
      promoCode: "",
      priority: 0,
      isActive: true,
      status: "Active",
      promoType: "Sale",
      stackingPolicy: "Allow",
    });
    setShowForm(false);
  };

  const columns = [
    { key: "name", label: "Name" },
    { key: "promoCode", label: "Promo Code" },
    { key: "promoType", label: "Type" },
    { key: "priority", label: "Priority" },
    { key: "status", label: "Status" },
    { key: "actions", label: "Actions" },
  ];

  const rows = paginatedPromotions.map((promotion) => {
    const promotionId = promotion.promotionId || promotion.id;
    const statusLabel =
      promotion.status || (promotion.isActive ? "Active" : "Inactive");
    const isActive =
      promotion.isActive !== undefined
        ? promotion.isActive
        : statusLabel === "Active";

    return {
      id: promotionId,
      name: promotion.name || promotion.promotionName || "-",
      promoCode: promotion.promoCode || promotion.code || "-",
      promoType: promotion.promoType || "-",
      priority: promotion.priority ?? 0,
      status: (
        <div className="flex items-center gap-2">
          <ActionCheckbox
            checked={isActive}
            label={isActive ? "Active" : "Inactive"}
            onChange={() => handleToggleActive(promotion)}
          />
          <span className="text-sm text-slate-700">
            {isActive ? "Active" : "Inactive"}
          </span>
        </div>
      ),
      actions: (
        <div className="flex items-center gap-3">
          <ActionButton
            label="Edit"
            icon="edit"
            onClick={() => handleEdit(promotion)}
          />
          <ActionButton
            label="Delete"
            icon="delete"
            tone="danger"
            onClick={() => handleDelete(promotion)}
          />
        </div>
      ),
    };
  });

  if (!selectedStoreId) {
    return (
      <div className="space-y-6">
        <PageHeader title="Promotions" subtitle="Manage promotional offers." />
        <Card>
          <Alert tone="warning">
            Please select a store to manage promotions.
          </Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Promotions"
        subtitle="Create and manage promotions."
        actions={
          showForm ? (
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Back
            </Button>
          ) : (
            <Button size="sm" onClick={() => setShowForm(true)}>
              Add Promotion
            </Button>
          )
        }
      />

      {showForm && (
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-slate-900">
            {editingId ? "Edit Promotion" : "Add Promotion"}
          </h2>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Promotion Name"
                name="name"
                placeholder="Summer Sale"
                value={form.name}
                onChange={handleChange}
                required
              />
              <Input
                label="Promo Code"
                name="promoCode"
                placeholder="SUMMER2024"
                value={form.promoCode}
                onChange={handleChange}
                helper="Optional discount code"
              />
            </div>

            <Textarea
              label="Description"
              name="description"
              placeholder="Get 20% off all items..."
              value={form.description}
              onChange={handleChange}
            />

            <div className="grid gap-4 md:grid-cols-2">
              <Select
                label="Status"
                name="status"
                value={form.status}
                onChange={handleChange}
                required
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Scheduled">Scheduled</option>
              </Select>
              <Select
                label="Promotion Type"
                name="promoType"
                value={form.promoType}
                onChange={handleChange}
                required
              >
                <option value="Sale">Sale</option>
                <option value="Coupon">Coupon</option>
                <option value="Loyalty">Loyalty</option>
                <option value="Bundle">Bundle</option>
              </Select>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Select
                label="Stacking Policy"
                name="stackingPolicy"
                value={form.stackingPolicy}
                onChange={handleChange}
                required
              >
                <option value="Allow">Allow with other promotions</option>
                <option value="Disallow">Disallow stacking</option>
                <option value="Restrict">Restrict to same type</option>
              </Select>
              <Input
                label="Priority"
                name="priority"
                type="number"
                placeholder="0"
                value={form.priority}
                onChange={handleChange}
                helper="Higher priority promotions apply first"
              />
            </div>

            <Checkbox
              label="Active"
              name="isActive"
              checked={form.isActive}
              onChange={handleChange}
            />

            {(createState.isSuccess || updateState.isSuccess) && (
              <Alert tone="success">
                Promotion {editingId ? "updated" : "created"} successfully.
              </Alert>
            )}
            {(createState.isError || updateState.isError) && (
              <Alert tone="error">
                {createState.error?.data?.detail ||
                  updateState.error?.data?.detail ||
                  "Failed to save promotion."}
              </Alert>
            )}

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={createState.isLoading || updateState.isLoading}
              >
                {editingId ? "Update" : "Add"} Promotion
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {!showForm && (
        <Card className="p-0">
          <div className="border-b border-slate-100 px-6 py-4 text-sm font-semibold text-slate-700">
            All Promotions
          </div>
          <div className="px-6 py-4">
            {isLoading && (
              <p className="text-sm text-slate-500">Loading promotions...</p>
            )}
            {isError && (
              <Alert tone="error">
                {error?.data?.detail || "Failed to load promotions."}
              </Alert>
            )}
            {!isLoading && !isError && (
              <>
                <Table
                  columns={columns}
                  rows={rows}
                  emptyText="No promotions yet."
                />
                {totalPages > 1 && (
                <Pagination
                  page={page}
                  pageSize={pageSize}
                  totalCount={totalCount}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                />
              )}
              </>
            )}
          </div>
        </Card>
      )}

      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({
            isOpen: false,
            promotionId: null,
            promotionName: "",
          })
        }
        title="Delete Promotion"
        confirmText="Delete"
        onConfirm={confirmDelete}
        isLoading={deleteState.isLoading}
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{deleteModal.promotionName}</strong>?
        </p>
      </Modal>
    </div>
  );
};

export default Promotions;
